// ./api/instructorAPI.js

import axiosInstance from '../utilities/axiosInstance';


const instructorAPI = {
  // ============================================================================
  // INSTRUCTOR PROFILE MANAGEMENT
  // ============================================================================

  // Get all instructors
  getAllInstructors: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/instructor/list/all', { params });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get instructors: ${error.message}` };
    }
  },

  // Create instructor
  createInstructor: async (loginId, instructorData) => {
    try {
      const response = await axiosInstance.post(`/api/instructor/${loginId}`, instructorData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to create instructorAPI: ${error.message}` };
    }
  },

  // Get instructor by loginId
  getInstructorByLoginId: async (loginId) => {
    try {
      const response = await axiosInstance.get(`/api/instructor/${loginId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get instructor: ${error.message}` };
    }
  },

  // Update instructor
  updateInstructor: async (loginId, updateData) => {
    try {
      const response = await axiosInstance.put(`/api/instructor/${loginId}`, updateData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to update instructor: ${error.message}` };
    }
  },

  // Delete (deactivate) instructor
  deleteInstructor: async (loginId, deletedBy) => {
    try {
      const response = await axiosInstance.delete(`/api/instructor/${loginId}`, {
        data: { deletedBy }
      });
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to delete instructor: ${error.message}` };
    }
  },

  // Login instructor
  loginInstructor: async (loginId) => {
    try {
      const response = await axiosInstance.post(`/api/instructor/${loginId}/login`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Login failed: ${error.message}` };
    }
  },

  // ============================================================================
  // COURSE ASSIGNMENT
  // ============================================================================

  // Assign course to instructor
  assignCourse: async (loginId, assignmentData) => {
    try {
      const response = await axiosInstance.post(
        `/api/instructor/${loginId}/assign-course`,
        assignmentData
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to assign course: ${error.message}` };
    }
  },

  // Unassign course from instructor
  unassignCourse: async (loginId, courseId, unassignedById) => {
    try {
      const response = await axiosInstance.delete(
        `/api/instructor/${loginId}/unassign-course`,
        { data: { courseId, unassignedById } }
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to unassign course: ${error.message}` };
    }
  },

  // Get assigned courses
  getAssignedCourses: async (loginId) => {
    try {
      const response = await axiosInstance.get(`/api/instructor/${loginId}/courses`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get courses: ${error.message}` };
    }
  },

  // Get course students
  getCourseStudents: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.get(
        `/api/instructor/${loginId}/courses/${courseId}/students`
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get students: ${error.message}` };
    }
  },

  // ============================================================================
  // LAB GRADING
  // ============================================================================

  // Get pending labs
  getPendingLabs: async (loginId, courseId = null) => {
    try {
      const params = courseId ? { courseId } : {};
      const response = await axiosInstance.get(`/api/instructor/${loginId}/labs/pending`, { params });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get pending labs: ${error.message}` };
    }
  },

  // Get graded labs
  getGradedLabs: async (loginId, courseId = null) => {
    try {
      const params = courseId ? { courseId } : {};
      const response = await axiosInstance.get(`/api/instructor/${loginId}/labs/graded`, { params });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get pending labs: ${error.message}` };
    }
  },

  // Get all labs for a course
  getCourseLabs: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.get(
        `/api/instructor/${loginId}/courses/${courseId}/labs`
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get labs: ${error.message}` };
    }
  },

  generateQuestionRubric: async (loginId, questionData) => {
    try {
      const response = await axiosInstance.post(
        `/api/instructor/${loginId}/generate-rubric`,
        questionData
      );
      return response.data;
    } catch (error) {
      return error?.response?.data || {
        success: false,
        message: error.message || 'Failed to generate rubric',
        error: `Failed to generate rubric API: ${error.message}`
      };
    }
  },

  generateCompleteLabRequirements: async (loginId, questionData) => {
    try {
      const response = await axiosInstance.post(
        `/api/instructor/${loginId}/generate-lab-requirements`,
        questionData
      );
      return response.data;
    } catch (error) {
      return error?.response?.data || {
        success: false,
        error: `Failed to generate IoT requirements: ${error.message}`
      };
    }
  },

  // Grade lab
  gradeLab: async (loginId, gradeData) => {
    try {
      const response = await axiosInstance.put(`/api/instructor/${loginId}/labs/grade`, gradeData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to grade lab: ${error.message}` };
    }
  },

  // ============================================================================
  // QUIZ GRADING
  // ============================================================================

  // Get pending quizzes
  getPendingQuizzes: async (loginId, courseId = null) => {
    try {
      const params = courseId ? { courseId } : {};
      const response = await axiosInstance.get(
        `/api/instructor/${loginId}/quizzes/pending`,
        { params }
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get pending quizzes: ${error.message}` };
    }
  },

  // Get all quizzes for a course
  getCourseQuizzes: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.get(
        `/api/instructor/${loginId}/courses/${courseId}/quizzes`
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get quizzes: ${error.message}` };
    }
  },

  // Grade quiz
  gradeQuiz: async (loginId, gradeData) => {
    try {
      const response = await axiosInstance.put(`/api/instructor/${loginId}/quizzes/grade`, gradeData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to grade quiz: ${error.message}` };
    }
  },

  // ============================================================================
  // EXAM GRADING
  // ============================================================================

  // Get pending exams
  getPendingExams: async (loginId, courseId = null) => {
    try {
      const params = courseId ? { courseId } : {};
      const response = await axiosInstance.get(
        `/api/instructor/${loginId}/exams/pending`,
        { params }
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get pending exams: ${error.message}` };
    }
  },

  // Get all exams for a course
  getCourseExams: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.get(
        `/api/instructor/${loginId}/courses/${courseId}/exams`
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get exams: ${error.message}` };
    }
  },

  // Grade exam
  gradeExam: async (loginId, gradeData) => {
    try {
      const response = await axiosInstance.put(`/api/instructor/${loginId}/exams/grade`, gradeData);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to grade exam: ${error.message}` };
    }
  },

  // ============================================================================
  // CAPSTONE GRADING
  // ============================================================================

  // Get pending capstones
  getPendingCapstones: async (loginId, courseId = null) => {
    try {
      const params = courseId ? { courseId } : {};
      const response = await axiosInstance.get(
        `/api/instructor/${loginId}/capstones/pending`,
        { params }
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get pending capstones: ${error.message}` };
    }
  },

  // Get all capstones for a course
  getCourseCapstones: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.get(
        `/api/instructor/${loginId}/courses/${courseId}/capstones`
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get capstones: ${error.message}` };
    }
  },

  // Grade capstone
  gradeCapstone: async (loginId, gradeData) => {
    try {
      const response = await axiosInstance.put(
        `/api/instructor/${loginId}/capstones/grade`,
        gradeData
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to grade capstone: ${error.message}` };
    }
  },

  // ============================================================================
  // ANALYTICS & DASHBOARD
  // ============================================================================

  // Get dashboard stats
  getDashboard: async (loginId) => {
    try {
      const response = await axiosInstance.get(`/api/instructor/${loginId}/dashboard`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get dashboard: ${error.message}` };
    }
  },

  // Get course analytics
  getCourseAnalytics: async (loginId, courseId) => {
    try {
      const response = await axiosInstance.get(
        `/api/instructor/${loginId}/courses/${courseId}/analytics`
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get analytics: ${error.message}` };
    }
  },

  // Get grading history
  getGradingHistory: async (loginId, params = {}) => {
    try {
      const response = await axiosInstance.get(
        `/api/instructor/${loginId}/grading-history`,
        { params }
      );
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get grading history: ${error.message}` };
    }
  },
};

export default instructorAPI;